import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, MessageSquare, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/** Keys for persistence across sessions. */
const LS_SHOWN = 'sms_optin_shown_v1';      // popup has been shown once
const LS_SUBMITTED = 'sms_optin_done_v1';   // user already subscribed (never show again)
const LS_DISMISSED_AT = 'sms_optin_dismissed_at_v1'; // timestamp of last close

/** Trigger rules — whichever fires first opens the popup. */
const IDLE_MS = 30_000;          // 30 s on site
const SCROLL_RATIO = 0.55;       // 55 % de la page scrollé (avant le footer pour ne pas bloquer ses liens)
const COOLDOWN_DAYS = 14;        // if dismissed, don't re-show before 14 days

const SMSOptInPopup = () => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);

  // Should we schedule triggers at all?
  const shouldTrigger = useCallback(() => {
    try {
      if (localStorage.getItem(LS_SUBMITTED)) return false;
      const dismissedAt = localStorage.getItem(LS_DISMISSED_AT);
      if (dismissedAt) {
        const days = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (days < COOLDOWN_DAYS) return false;
      }
    } catch (e) {
      /* no localStorage → skip popup politely */
      return false;
    }
    return true;
  }, []);

  const triggerOpen = useCallback(() => {
    if (!shouldTrigger()) return;
    try {
      localStorage.setItem(LS_SHOWN, '1');
    } catch (e) { /* ignore */ }
    setOpen(true);
  }, [shouldTrigger]);

  // Set up idle + scroll triggers
  useEffect(() => {
    if (!shouldTrigger()) return;
    const idleTimer = setTimeout(triggerOpen, IDLE_MS);
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = (h.scrollTop + window.innerHeight) / h.scrollHeight;
      if (scrolled >= SCROLL_RATIO) {
        triggerOpen();
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(idleTimer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [triggerOpen, shouldTrigger]);

  // ESC to close + click outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    try { localStorage.setItem(LS_DISMISSED_AT, String(Date.now())); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!phone.trim()) {
      setError('Le numéro est requis.');
      return;
    }
    if (!consent) {
      setError('Merci de cocher la case de consentement.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/sms/opt-in`, {
        name: name.trim(),
        phone: phone.trim(),
        consent: true,
        source: 'opt-in-popup',
      });
      setSubmitted(true);
      try {
        localStorage.setItem(LS_SUBMITTED, '1');
        localStorage.setItem(LS_DISMISSED_AT, String(Date.now()));
      } catch (err) { /* ignore */ }
      // Auto-close after 4s
      setTimeout(() => setOpen(false), 4000);
      // Hide error just in case
      if (data?.already_subscribed) {
        /* already-subscribed is still a success for UX */
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur, réessayez dans un moment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sms-optin-title"
      data-testid="sms-optin-popup"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* card */}
      <div
        ref={dialogRef}
        className="relative w-full sm:w-[480px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full hover:bg-black/5 transition-colors"
          aria-label="Fermer"
          data-testid="sms-optin-close"
        >
          <X className="w-5 h-5 text-[#5D4E37]" />
        </button>

        {/* Header band */}
        <div className="bg-gradient-to-r from-[#C45C26] to-[#A64C1F] p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MessageSquare className="w-5 h-5" />
            </div>
            <h2 id="sms-optin-title" className="text-lg sm:text-xl font-bold leading-tight">
              Restez informés par SMS
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6">
          {submitted ? (
            <div className="text-center py-4" data-testid="sms-optin-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#3D2E1F] mb-1">C'est fait, merci !</h3>
              <p className="text-sm text-[#5D4E37] leading-relaxed">
                Vous allez recevoir un SMS de bienvenue dans quelques instants.
                <br />
                Pour vous désinscrire, répondez simplement <strong>STOP</strong> à l'un de nos messages.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#5D4E37] mb-3 leading-relaxed">
                Recevez en direct les infos utiles de la Pharmacie Saint Ange Des Amis :
              </p>
              <ul className="text-sm text-[#5D4E37] mb-4 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-[#C45C26] mt-0.5">✓</span>
                  Alertes quand nous sommes de garde (24h/24)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C45C26] mt-0.5">✓</span>
                  Arrivées de produits commandés (France, Suisse)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C45C26] mt-0.5">✓</span>
                  Promos parapharmacie & laits infantiles
                </li>
              </ul>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label htmlFor="sms-optin-name" className="text-xs text-[#8B7355]">
                    Prénom (optionnel)
                  </Label>
                  <Input
                    id="sms-optin-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nathalie"
                    className="mt-0.5"
                    data-testid="sms-optin-name"
                  />
                </div>
                <div>
                  <Label htmlFor="sms-optin-phone" className="text-xs text-[#8B7355]">
                    Numéro de téléphone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="sms-optin-phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07 57 91 06 63"
                    className="mt-0.5"
                    required
                    data-testid="sms-optin-phone"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-[#5D4E37] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#C45C26]"
                    data-testid="sms-optin-consent"
                  />
                  <span>
                    J'accepte de recevoir des SMS de la Pharmacie Saint Ange Des Amis.
                    Je peux me désinscrire à tout moment en répondant STOP.
                  </span>
                </label>

                {error && (
                  <p className="text-xs text-red-600" data-testid="sms-optin-error">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={submitting || !phone || !consent}
                  className="w-full bg-[#C45C26] hover:bg-[#A64C1F] text-white"
                  data-testid="sms-optin-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Inscription...</>
                  ) : (
                    <>S'inscrire gratuitement</>
                  )}
                </Button>

                <p className="text-[10px] text-[#8B7355] text-center leading-relaxed">
                  🔒 Votre numéro n'est jamais partagé. Aucun spam, ~2 SMS par mois maximum.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMSOptInPopup;
